import { useState, useEffect, useCallback } from 'react';
import {
  Notification,
  getNotifications,
  markRead,
  markAllRead,
  dismiss,
  restore,
  dismissAllRead,
  takeAction as takeActionApi,
} from '../services/notificationsService';

const POLL_INTERVAL_MS = 60_000;

/**
 * @param enabled set false for roles with no access to notifications, so the
 *   shell does not poll an endpoint it will only ever be refused. Nothing
 *   creates booking notifications today, so guests lose nothing by skipping it.
 */
export function useNotifications(enabled: boolean = true) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data);
      setError(null);
    } catch (err) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetch();
    const interval = setInterval(fetch, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetch, enabled]);

  const handleMarkRead = useCallback(async (notificationId: string) => {
    await markRead(notificationId);
    setNotifications(prev =>
      prev.map(n => n.notificationId === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  const handleTakeAction = useCallback(async (notificationId: string, action: 'ignored' | 'purged') => {
    const result = await takeActionApi(notificationId, action);
    setNotifications(prev =>
      prev.map(n =>
        n.notificationId === notificationId
          ? { ...n, actionTaken: action, actionTakenAt: new Date().toISOString(), read: true }
          : n
      )
    );
    return result;
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    await markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  /** Removes the notification straight away and puts it back if the server refuses. */
  const handleDismiss = useCallback(async (notification: Notification) => {
    setNotifications(prev => prev.filter(n => n.notificationId !== notification.notificationId));
    try {
      await dismiss(notification.notificationId);
    } catch (err) {
      setNotifications(prev => insertByDate(prev, notification));
      throw err;
    }
  }, []);

  const handleRestore = useCallback(async (notification: Notification) => {
    await restore(notification.notificationId);
    setNotifications(prev => insertByDate(prev, { ...notification, read: true }));
  }, []);

  /** Returns the cleared ids so the caller can offer Undo through restoreMany. */
  const handleDismissAllRead = useCallback(async () => {
    const ids = await dismissAllRead();
    setNotifications(prev => prev.filter(n => !ids.includes(n.notificationId)));
    return ids;
  }, []);

  const handleRestoreMany = useCallback(async (ids: string[]) => {
    await Promise.all(ids.map(id => restore(id)));
    await fetch();
  }, [fetch]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refetch: fetch,
    markRead: handleMarkRead,
    markAllRead: handleMarkAllRead,
    dismiss: handleDismiss,
    restore: handleRestore,
    dismissAllRead: handleDismissAllRead,
    restoreMany: handleRestoreMany,
    takeAction: handleTakeAction,
  };
}

function insertByDate(list: Notification[], notification: Notification): Notification[] {
  return [...list.filter(n => n.notificationId !== notification.notificationId), notification]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
